import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Television, Timer, MapPin, Horse, ChartBar, Trophy, Star, Medal, Fire, TrendUp, Play,
} from "@phosphor-icons/react";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import LiveStreamPlayer from "./LiveStreamPlayer";

const WATCH_LINKS = {
  "ITV": { url: "https://www.itv.com/watch/simulcast/itv", label: "ITV", free: true },
  "ITV4": { url: "https://www.itv.com/watch/simulcast/itv4", label: "ITV4", free: true },
  "Sky Sports Racing": { url: "https://www.skysports.com/watch/sky-sports-racing", label: "Sky Sports Racing", free: false },
  "Racing TV": { url: "https://www.racingtv.com/live", label: "Racing TV", free: false },
};

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/c9755703-c0c8-4c44-9e75-4d569fefe3eb/images/b5b9ca868abc47efd9041c96bdb5eb686bc993cf20496e60995133b3852f9312.png";

function WinChanceBadge({ chance, isFavourite }) {
  let bg = "bg-zinc-200 text-zinc-700";
  if (chance >= 30) bg = "bg-[#008A27] text-white";
  else if (chance >= 20) bg = "bg-[#00A832] text-white";
  else if (chance >= 10) bg = "bg-amber-500 text-white";
  else if (chance >= 5) bg = "bg-zinc-400 text-white";

  return (
    <div className="flex flex-col items-end gap-0.5">
      {isFavourite && (
        <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-[#008A27] flex items-center gap-0.5">
          <Star weight="fill" size={10} /> Most Likely
        </span>
      )}
      <span className={`px-3 py-1.5 font-mono text-sm font-bold ${bg}`}>
        {chance}%
      </span>
    </div>
  );
}

export default function LiveRaceHero({ races }) {
  const getDefaultRaceIndex = (raceList) => {
    if (!raceList?.length) return 0;
    const liveIdx = raceList.findIndex(r => r.status === "LIVE");
    if (liveIdx >= 0) return liveIdx;
    const nextIdx = raceList.findIndex(r => r.status === "Next");
    if (nextIdx >= 0) return nextIdx;
    const upcomingIdx = raceList.findIndex(r => r.status === "Upcoming");
    if (upcomingIdx >= 0) return upcomingIdx;
    const resultIndices = raceList.map((r, i) => r.status === "Result" ? i : -1).filter(i => i >= 0);
    if (resultIndices.length > 0) return resultIndices[resultIndices.length - 1];
    return 0;
  };

  const [selectedRace, setSelectedRace] = useState(0);
  const [autoSelected, setAutoSelected] = useState(false);
  const [showStream, setShowStream] = useState(false);
  const race = races?.[selectedRace];
  const watchInfo = race?.channel ? WATCH_LINKS[race.channel] : null;

  // Auto-jump to the live/next/upcoming race when data loads
  useEffect(() => {
    if (races?.length && !autoSelected) {
      const idx = getDefaultRaceIndex(races);
      setSelectedRace(idx);
      setAutoSelected(true);
      setTimeout(() => {
        const el = document.getElementById(`race-tab-${idx}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }, 300);
    }
  }, [races, autoSelected]);

  if (!races?.length) {
    return (
      <div data-testid="live-race-hero-empty" className="border border-border bg-zinc-50 p-8 text-center">
        <p className="mono-label text-zinc-400">No races scheduled today</p>
      </div>
    );
  }

  return (
    <div data-testid="live-race-hero" className="border border-border">

      {/* Hero Image with Watch Live overlay */}
      <div className="relative h-[280px] md:h-[360px] overflow-hidden">
        <img src={HERO_BG} alt="Racing" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

        <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
          {race?.status === "LIVE" && (
            <span data-testid="live-indicator" className="flex items-center gap-1.5 bg-[#E12B00] text-white px-3 py-1 text-xs font-mono uppercase tracking-widest font-bold">
              <span className="w-2 h-2 bg-white rounded-full live-pulse" />
              Live
            </span>
          )}
          {race?.status !== "LIVE" && (
            <span className="flex items-center gap-1.5 bg-zinc-800 text-white px-3 py-1 text-xs font-mono uppercase tracking-widest font-bold">{race?.status}</span>
          )}
          <span className="bg-black/70 backdrop-blur-xl text-white px-3 py-1 text-xs font-mono flex items-center gap-1.5">
            <Television weight="bold" size={14} />{race?.channel}
          </span>
        </div>

        <div className="absolute top-4 right-4 z-10">
          <span className="bg-[#008A27]/90 backdrop-blur-sm text-white px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold flex items-center gap-1.5">
            <ChartBar weight="bold" size={12} />
            Win chance from form, ratings, jockey + owner stats
          </span>
        </div>

        {/* Big play button - opens in-app stream player */}
        {watchInfo && (
          <button
            onClick={() => setShowStream(true)}
            data-testid="play-stream-btn"
            className="absolute inset-0 flex items-center justify-center z-[5] group cursor-pointer"
          >
            <div className="flex flex-col items-center gap-2">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#E12B00] flex items-center justify-center group-hover:bg-[#C52400] group-hover:scale-110 transition-all shadow-lg shadow-red-900/30">
                <Play weight="fill" size={32} className="text-white ml-1" />
              </div>
              <span className="bg-black/80 backdrop-blur-sm text-white px-4 py-2 text-xs font-mono uppercase tracking-wider font-bold flex items-center gap-2">
                <Television weight="bold" size={14} />
                Watch Live on {watchInfo.label}
                {watchInfo.free && <span className="bg-[#008A27] px-1.5 py-0.5 text-[9px]">FREE</span>}
              </span>
            </div>
          </button>
        )}

        {/* In-App Stream Player Modal */}
        {showStream && race && (
          <LiveStreamPlayer race={race} onClose={() => setShowStream(false)} />
        )}

        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 z-10">
          <p className="mono-label text-zinc-400 mb-1">{race?.track_name} &mdash; {race?.time}</p>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-heading font-black text-white tracking-tight mb-2">{race?.race_name}</h2>
          <div className="flex items-center gap-4 text-zinc-300 text-sm font-mono flex-wrap">
            <span className="flex items-center gap-1"><MapPin weight="bold" size={14} />{race?.track_name}</span>
            {race?.region && <span className="bg-white/20 px-1.5 py-0.5 text-[10px] font-bold">{race.region}</span>}
            <span className="flex items-center gap-1"><Timer weight="bold" size={14} />{race?.distance}</span>
            <span>{race?.going}</span>
            {race?.prize && <span className="text-[#008A27] font-bold">{race?.prize}</span>}
          </div>
        </div>
      </div>

      {/* Race Tabs */}
      <ScrollArea className="w-full">
        <div className="flex border-b border-border" data-testid="race-selector-tabs">
          {races.map((r, i) => {
            const isLive = r.status === "LIVE";
            const isNext = r.status === "Next";
            const isSelected = i === selectedRace;
            const isResult = r.status === "Result";

            let tabBg = "bg-white text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900";
            if (isSelected) tabBg = "bg-[#09090B] text-white";
            else if (isLive) tabBg = "bg-red-50 text-[#E12B00] border-b-2 border-[#E12B00]";
            else if (isNext) tabBg = "bg-amber-50 text-amber-700 border-b-2 border-amber-500";
            else if (isResult) tabBg = "bg-zinc-50 text-zinc-400";

            return (
              <button
                key={r.id}
                data-testid={`race-tab-${i}`}
                id={`race-tab-${i}`}
                onClick={() => setSelectedRace(i)}
                className={`flex-shrink-0 px-4 py-3 text-xs font-mono uppercase tracking-wider border-r border-border transition-colors ${tabBg}`}
              >
                {isLive && (
                  <span className="flex items-center justify-center gap-1 mb-1">
                    <span className="w-2.5 h-2.5 bg-[#E12B00] rounded-full live-pulse" />
                    <span className="text-[10px] font-bold text-[#E12B00]">LIVE NOW</span>
                  </span>
                )}
                {isNext && !isSelected && (
                  <span className="flex items-center justify-center gap-1 mb-1">
                    <span className="w-2 h-2 bg-amber-500 rounded-full" />
                    <span className="text-[10px] font-bold text-amber-600">NEXT UP</span>
                  </span>
                )}
                <span className="block font-bold">{r.time}</span>
                <span className="block text-[10px] mt-0.5">{r.track_name}</span>
                {r.region && <span className="block text-[9px] mt-0.5 opacity-60">{r.region}</span>}
              </button>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Runners */}
      <div className="p-4">
        {/* Result banner for finished races */}
        {race?.result && race.result.length > 0 && (
          <div data-testid="race-result-banner" className="mb-4 border-2 border-[#008A27] bg-green-50 p-4">
            <p className="mono-label text-[#008A27] mb-2 flex items-center gap-1.5">
              <Trophy weight="fill" size={14} />
              Race Result
            </p>
            <div className="space-y-2">
              {race.result.map((r, i) => (
                <div key={i} className={`flex items-center justify-between ${i === 0 ? 'text-base' : 'text-sm text-zinc-500'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono font-bold w-8 h-8 flex items-center justify-center text-sm ${
                      i === 0 ? 'bg-[#008A27] text-white' : i === 1 ? 'bg-zinc-200 text-zinc-700' : 'bg-zinc-100 text-zinc-500'
                    }`}>
                      {i === 0 ? '1st' : i === 1 ? '2nd' : '3rd'}
                    </span>
                    <div>
                      <span className={`font-semibold ${i === 0 ? 'text-[#008A27] text-lg' : ''}`}>
                        {i === 0 && <Trophy weight="fill" size={16} className="inline mr-1 text-[#008A27]" />}
                        {r.horse}
                      </span>
                      {r.jockey && <span className="text-xs text-zinc-400 ml-2">{r.jockey}</span>}
                    </div>
                  </div>
                  {r.sp && <span className="font-mono text-xs">{r.sp}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <p className="mono-label text-zinc-400">
            <Horse weight="bold" size={12} className="inline mr-1" />
            {race?.result ? 'Runners & Predictions' : `Runners (${race?.runners?.length})`} &mdash; sorted by win chance
          </p>
          <p className="text-[10px] font-mono text-zinc-400">
            % chance based on form, jockey/owner win rates + official ratings
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {race?.runners?.map((runner, idx) => {
            const co = runner.calculated_odds;
            const chance = co?.win_chance_pct || 0;
            const isFav = runner.is_favourite;

            return (
              <Link
                key={runner.horse_id}
                to={`/horse/${runner.horse_id}`}
                data-testid={`live-race-runner-${idx}`}
                className={`border hover:bg-zinc-800/50 transition-colors group p-3 ${
                  isFav ? "border-[#008A27] bg-[#008A27]/10" : runner.is_most_backed ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-zinc-800"
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs font-bold text-zinc-400 w-5">{idx + 1}</span>
                    <div>
                      <p className={`text-sm font-semibold transition-colors ${
                        isFav ? "text-[#008A27] group-hover:text-[#00C040]" : "text-white group-hover:text-[#D4AF37]"
                      }`}>
                        {runner.horse_name}
                        {isFav && <Star weight="fill" size={12} className="inline ml-1 text-[#008A27]" />}
                      </p>
                      <p className="text-xs text-zinc-500">{runner.jockey} &middot; {runner.trainer}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {isFav && (
                          <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-white bg-[#008A27] px-1.5 py-0.5 flex items-center gap-0.5">
                            <Star weight="fill" size={9} /> Most Likely
                          </span>
                        )}
                        {runner.is_most_backed && (
                          <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-white bg-amber-500 px-1.5 py-0.5 flex items-center gap-0.5">
                            <Fire weight="fill" size={9} /> Most Backed
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <WinChanceBadge chance={chance} isFavourite={isFav} />
                    {runner.betfred_odds && (
                      <div className="flex flex-col items-center bg-blue-600 text-white px-2 py-1 text-center">
                        <span className="text-[8px] font-mono font-bold uppercase">Betfred</span>
                        <span className="font-mono text-sm font-bold">{runner.betfred_odds.fractional || runner.betfred_odds.decimal}</span>
                      </div>
                    )}
                  </div>
                </div>

                {co && (
                  <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-400 flex-wrap">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex items-center gap-1 cursor-help border-b border-dotted border-zinc-300">
                            <Trophy weight="fill" size={10} className="text-[#008A27]" />
                            {co.wins}W {co.places}P / {co.races_analysed}R
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="bg-[#09090B] text-white border-none font-mono text-xs p-3 max-w-[220px]">
                          <p className="font-bold mb-1">{runner.horse_name}</p>
                          <p>Wins: {co.wins} from {co.races_analysed} races</p>
                          <p>Places (2nd/3rd): {co.places}</p>
                          <p>Win Rate: {co.win_rate_pct}%</p>
                          <p>Place Rate: {co.place_rate_pct}%</p>
                          {co.official_rating > 0 && <p>Official Rating: {co.official_rating}</p>}
                          {co.rpr > 0 && <p>RPR: {co.rpr}</p>}
                          {co.jockey_win_rate > 0 && <p className="text-cyan-400">Jockey Win Rate: {co.jockey_win_rate}%</p>}
                          {co.owner_win_rate > 0 && <p className="text-purple-400">Owner Win Rate: {co.owner_win_rate}%</p>}
                          <p className="mt-1 font-bold text-[#008A27]">Win Chance: {co.win_chance_pct}%</p>
                          {runner.market_data?.num_bookmakers > 0 && (
                            <>
                              <p className="mt-1 border-t border-zinc-700 pt-1 text-amber-400">Bookmaker Data ({runner.market_data.num_bookmakers} platforms):</p>
                              <p>Avg Odds: {runner.market_data.avg_bookmaker_odds}</p>
                              <p>Shortest: {runner.market_data.shortest_odds} | Longest: {runner.market_data.longest_odds}</p>
                            </>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <span className="text-zinc-300">|</span>
                    <span>{co.win_rate_pct}% win rate</span>
                    {co.official_rating > 0 && (
                      <>
                        <span className="text-zinc-300">|</span>
                        <span className="flex items-center gap-0.5"><Medal weight="fill" size={10} className="text-amber-500" />OR: {co.official_rating}</span>
                      </>
                    )}
                    {runner.market_data?.num_bookmakers > 0 && (
                      <>
                        <span className="text-zinc-300">|</span>
                        <span className="flex items-center gap-0.5">
                          <TrendUp weight="bold" size={10} className="text-amber-500" />
                          {runner.market_data.num_bookmakers} bookies avg {runner.market_data.avg_bookmaker_odds}
                        </span>
                      </>
                    )}
                    {co.jockey_win_rate > 0 && (
                      <>
                        <span className="text-zinc-300">|</span>
                        <span className="text-cyan-600">J: {co.jockey_win_rate}%</span>
                      </>
                    )}
                    {co.owner_win_rate > 0 && (
                      <>
                        <span className="text-zinc-300">|</span>
                        <span className="text-purple-600">O: {co.owner_win_rate}%</span>
                      </>
                    )}
                    <span className="text-zinc-300">|</span>
                    <span>Form: {runner.form}</span>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
